
import Vue from 'vue';
import organizationApi from "@/services/api.organizationSettings.ts";
import { Getter, State, Action } from "vuex-class";
import { NotificationSystemPrefs, TenantSystemPrefs } from "@/store/types";
import Component from "vue-class-component";
import EventOutcomeSelect from "@/components/form/EventOutcomeSelect.vue";
import vSelect3 from "vselect3";
import { numberFormattingMixins } from '@/mixins';
import NotificationTemplateFormatOption, { notificationTemplateFormats } from "@/types/sv-data/notification-templates/NotificationTemplateFormatOption";
import NotificationDocumentTypeId from '@/types/sv-data/enums/NotificationDocumentTypeIds';

const { enforceWholeNumber } = numberFormattingMixins.methods;

@Component({
	components:{
		"event-outcome-select": EventOutcomeSelect,
		"v-select-3": vSelect3,
	}
})

export default class NotificationSystemPrefsForm extends Vue {
	// Getters
	@Getter getNotificationTemplateDefaultFormat: number;
	@Getter getFeature: (featuresList: string[]) => boolean;
	@Getter getNotificationSystemPrefs: NotificationSystemPrefs;

	@Action("getNotificationSystemPrefs") loadNotificationSystemPrefs: (live: boolean) => Promise<void>;
	@Action createOrUpdateTenantNotificationSystemPrefs: (updatedPrefs: NotificationSystemPrefs) => Promise<void>;

	@State("tenantSystemPrefs") tenantSystemPrefs: TenantSystemPrefs;

	private get isNotificationTemplatesEmbeddedHtmlEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "EmbeddedHtml"]);
	}

	public get isReplyToEmailAddressEnabled(): boolean {
		return this.getFeature(["TenantSettings", "ReplyToEmailAddress"]);
	}

	private get isSendToAllInSingleEmailEnabled(): boolean
	{
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "SendToAllInSingleEmail"]);
	}

	private get haveSettingsChanged(): boolean {
		const valuesChanged = this.notificationSettings?.notificationTemplateDefaultFormat !== this.newDefaultNotificationTemplateFormat.id ||
			this.notificationSettings?.replyToEmailAddress !== this.newReplyToEmailAddress ||
			this.notificationSettings?.notificationsDefaultToSendInSingleEmail !== this.defaultSendToAllInSingleEmail;

		this.updateToDate = !valuesChanged;

		return valuesChanged;
	}

	private notificationSystemPrefs: NotificationSystemPrefs;

	private notificationSettings: NotificationSystemPrefs = {
		notificationTemplateDefaultFormat: NotificationDocumentTypeId.Png,
		replyToEmailAddress: null,
	};

	private notificationTemplateFormats: NotificationTemplateFormatOption[] = notificationTemplateFormats;
	private newDefaultNotificationTemplateFormat: NotificationTemplateFormatOption = { id: NotificationDocumentTypeId.Png, label: "Embedded" };
	private newReplyToEmailAddress: string | null = null;
	private defaultSendToAllInSingleEmail: boolean | null = null;
	private updateToDate: boolean = true;

	private validEmailRegex: RegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;

	private async beforeMount(): Promise<void> {
		try {
			this.loadNotificationSystemPrefs(true);
			this.notificationSystemPrefs = this.getNotificationSystemPrefs;
		} catch (e) {
			console.error("Failed to get notification system prefs");
			this.$notify({
				type: "error",
				title: "Notification settings",
				text: "Unable to retrieve notification settings - please try again later, or contact support if the problem persists"
			});
		}

		this.resetChanges();
	}

	private async resetChanges(): Promise<void> {
		let defaultTemplateFormatIndex = this.notificationTemplateFormats.findIndex(f => f.id === this.getNotificationTemplateDefaultFormat);

		if (!this.notificationSettings) {
			this.newDefaultNotificationTemplateFormat = this.notificationTemplateFormats[defaultTemplateFormatIndex];
			this.newReplyToEmailAddress = null;
			this.defaultSendToAllInSingleEmail = null;
			return;
		}

		this.notificationSettings.notificationTemplateDefaultFormat = this.notificationSystemPrefs?.notificationTemplateDefaultFormat;
		this.notificationSettings.replyToEmailAddress = this.notificationSystemPrefs?.replyToEmailAddress;
		this.notificationSettings.notificationsDefaultToSendInSingleEmail = this.notificationSystemPrefs?.notificationsDefaultToSendInSingleEmail;

		if (this.notificationSettings?.notificationTemplateDefaultFormat)
		{
			defaultTemplateFormatIndex = this.notificationTemplateFormats.findIndex(f => f.id === this.notificationSettings.notificationTemplateDefaultFormat)
											?? defaultTemplateFormatIndex;
		}

		this.newDefaultNotificationTemplateFormat = this.notificationTemplateFormats[defaultTemplateFormatIndex];
		this.newReplyToEmailAddress = this.notificationSettings.replyToEmailAddress;
		this.defaultSendToAllInSingleEmail = this.notificationSettings.notificationsDefaultToSendInSingleEmail;

		this.updateToDate = true;
	}

	private get isReplyToEmailValid(): boolean {
		if (!this.newReplyToEmailAddress || this.newReplyToEmailAddress === "") {
			return true;
		}

		return this.validEmailRegex.test(this.newReplyToEmailAddress);
	}

	private async confirmUpdateNotificationSettings(): Promise<void> {
		const newNotificationSettings: NotificationSystemPrefs = {
			notificationTemplateDefaultFormat: this.newDefaultNotificationTemplateFormat.id,
			replyToEmailAddress: this.newReplyToEmailAddress,
			notificationsDefaultToSendInSingleEmail: this.defaultSendToAllInSingleEmail,
		};

		try {
			await this.createOrUpdateTenantNotificationSystemPrefs(newNotificationSettings);

			this.notificationSystemPrefs = this.getNotificationSystemPrefs;
		} catch (e) {
			this.$notify({
				type: "error",
				title: "Notification settings",
				text: "Unable to update notification settings - please try again later, or contact support if the problem persists"
			});
			return;
		}

		this.$notify({
			type: "success",
			title: "Notification settings updated"
		});

		this.resetChanges();
	}
}
